import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { supabase } from '../../../lib/supabaseClient';
import { getFD100ThirdEditionWinners } from '../../../api';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const EditionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 24px;
    margin-bottom: 40px;
`;

const EditionCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    border-left: 5px solid ${props => props.borderColor || '#667eea'};

    h3 {
        font-size: 20px;
        margin: 0 0 16px 0;
        color: #333;
    }
`;

const StatRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;

    .label {
        color: #666;
    }

    .value {
        font-weight: 600;
        color: ${props => props.highlight ? '#27ae60' : '#333'};
    }
`;

const ExportSection = styled.div`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.3);
`;

const ExportButton = styled.button`
    padding: 16px 40px;
    background: white;
    color: #667eea;
    border: none;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0,0,0,0.15);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(0,0,0,0.2);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const SUPABASE_EDITIONS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': { name: '1ère édition', order: 1 },
    '417b2020-721a-4c55-bd9f-d86a913f26ad': { name: '2ème édition', order: 2 }
};

const THIRD_EDITION_ID = '6418b274494e3ee26a9076ac';

const FD100EditionsExport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [editions, setEditions] = useState([]);

    useEffect(() => {
        loadAllEditions();
    }, []);

    const loadAllEditions = async () => {
        try {
            setLoading(true);
            const editionsData = [];

            // Load 1st and 2nd editions from Supabase
            for (const [eventId, info] of Object.entries(SUPABASE_EDITIONS)) {
                const { data: winners, error } = await supabase
                    .from('winners')
                    .select('*')
                    .eq('event_id', eventId)
                    .order('company_name', { ascending: true });

                if (error) {
                    console.error(`Error loading ${info.name}:`, error);
                    continue;
                }

                editionsData.push({
                    id: eventId,
                    name: info.name,
                    order: info.order,
                    source: 'supabase',
                    winners: winners || [],
                    stats: {
                        total: (winners || []).length,
                        withEmail: (winners || []).filter(w => w.email).length,
                        onPlatform: (winners || []).filter(w => w.user_id).length,
                        withProject: (winners || []).filter(w => w.project_id).length
                    }
                });
            }

            // Load 3rd edition from MongoDB
            try {
                const response = await getFD100ThirdEditionWinners();
                const participations = response.data || [];

                const transformedWinners = [];
                participations.forEach(participation => {
                    if (participation.participation?.data?.projets) {
                        participation.participation.data.projets.forEach(project => {
                            if (typeof project === 'object' && project !== null && project.statusFinal === 'validé') {
                                const companyProjects = participation.company?.projects || [];
                                let realProject = companyProjects.find(p => p._id === project.id_in_company);
                                if (!realProject) realProject = companyProjects.find(p => p._id === project._id);
                                if (!realProject) {
                                    const projectName = (project.title || project.name || '').toLowerCase().trim();
                                    if (projectName) {
                                        realProject = companyProjects.find(p =>
                                            (p.name || '').toLowerCase().trim() === projectName ||
                                            (p.name_en || '').toLowerCase().trim() === projectName
                                        );
                                    }
                                }

                                const firstName = participation.hsProfile?.firstname || participation.user?.first_name || '';
                                const lastName = participation.hsProfile?.lastname || participation.user?.last_name || '';
                                const companyName = participation.hsProfile?.company || participation.user?.companyname || '';
                                const email = participation.user?.email || '';

                                transformedWinners.push({
                                    project_name: project.title || project.name || 'Sans nom',
                                    company_name: companyName || `${firstName} ${lastName}`.trim() || 'Inconnu',
                                    first_name: firstName,
                                    last_name: lastName,
                                    email: email,
                                    phone: '',
                                    user_id: participation.user?._id || null,
                                    project_id: realProject?._id || null,
                                    company_id: realProject?._matchedFromCompanyId || participation.company?._id || null,
                                    category: project.categorie || project.type || '',
                                    type: project.type || '',
                                    medias_count: realProject?.medias?.length || 0,
                                    hs_company: participation.hsProfile?.company || '',
                                    participation_id: participation.participation?._id || ''
                                });
                            }
                        });
                    }
                });

                editionsData.push({
                    id: THIRD_EDITION_ID,
                    name: '3ème édition',
                    order: 3,
                    source: 'mongodb',
                    winners: transformedWinners,
                    stats: {
                        total: transformedWinners.length,
                        withEmail: transformedWinners.filter(w => w.email).length,
                        onPlatform: transformedWinners.filter(w => w.user_id).length,
                        withProject: transformedWinners.filter(w => w.project_id).length
                    }
                });
            } catch (error) {
                console.error('Error loading 3rd edition:', error);
            }

            setEditions(editionsData.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error('Error loading editions:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const exportToXLSX = () => {
        setExporting(true);

        try {
            const wb = XLSX.utils.book_new();

            editions.forEach(edition => {
                const isMongo = edition.source === 'mongodb';

                const rows = edition.winners.map((w, idx) => {
                    const baseRow = {
                        '#': idx + 1,
                        'Projet': w.project_name || '',
                        'Entreprise': w.company_name || '',
                        'Prénom': w.first_name || '',
                        'Nom': w.last_name || '',
                        'Email': w.email || '',
                        'Téléphone': w.phone || '',
                        'Sur la plateforme': w.user_id ? 'Oui' : 'Non',
                        'Projet dans annuaire': w.project_id ? 'Oui' : 'Non',
                        'Lien fiche projet': w.project_id && w.company_id
                            ? `https://connectedby.lefrenchdesign.org/project?pid=${w.project_id}&cid=${w.company_id}`
                            : '',
                        'Lien fiche agence': w.company_id
                            ? `https://connectedby.lefrenchdesign.org/company?id=${w.company_id}`
                            : ''
                    };

                    if (isMongo) {
                        baseRow['Catégorie'] = w.category || '';
                        baseRow['Type'] = w.type || '';
                        baseRow['Nb médias'] = w.medias_count || 0;
                        baseRow['Entreprise (HubSpot)'] = w.hs_company || '';
                    }

                    return baseRow;
                });

                const ws = XLSX.utils.json_to_sheet(rows);

                // Auto-size columns
                const colWidths = {};
                rows.forEach(row => {
                    Object.entries(row).forEach(([key, val]) => {
                        const len = Math.max(String(val || '').length, key.length);
                        colWidths[key] = Math.min(Math.max(colWidths[key] || 0, len), 50);
                    });
                });
                ws['!cols'] = Object.values(colWidths).map(w => ({ wch: w + 2 }));

                const sheetName = `FD100 ${edition.name}`.substring(0, 31);
                XLSX.utils.book_append_sheet(wb, ws, sheetName);
            });

            // Add summary sheet
            const summaryRows = editions.map(ed => ({
                'Édition': ed.name,
                'Total lauréats': ed.stats.total,
                'Avec email': ed.stats.withEmail,
                'Sur la plateforme': ed.stats.onPlatform,
                'Projet dans annuaire': ed.stats.withProject,
                '% sur plateforme': ed.stats.total > 0
                    ? `${Math.round((ed.stats.onPlatform / ed.stats.total) * 100)}%`
                    : '0%',
                '% projet lié': ed.stats.total > 0
                    ? `${Math.round((ed.stats.withProject / ed.stats.total) * 100)}%`
                    : '0%'
            }));

            const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
            summaryWs['!cols'] = [
                { wch: 15 }, { wch: 15 }, { wch: 12 },
                { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 15 }
            ];
            XLSX.utils.book_append_sheet(wb, summaryWs, 'Résumé');

            const dateStr = new Date().toISOString().split('T')[0];
            XLSX.writeFile(wb, `FD100_3_editions_laureats_${dateStr}.xlsx`);

            toast.success('Export XLSX téléchargé');
        } catch (error) {
            console.error('Error exporting XLSX:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <Spinner />;

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>

            <Header>
                <h1>FD100 - Export toutes éditions</h1>
                <div className="subtitle">
                    Données consolidées des 3 premières éditions du French Design 100
                </div>
            </Header>

            <EditionsGrid>
                {editions.map(edition => (
                    <EditionCard key={edition.id} borderColor={
                        edition.order === 1 ? '#ffd700' :
                        edition.order === 2 ? '#c0c0c0' :
                        '#cd7f32'
                    }>
                        <h3>FD100 - {edition.name}</h3>
                        <StatRow>
                            <span className="label">Total lauréats</span>
                            <span className="value">{edition.stats.total}</span>
                        </StatRow>
                        <StatRow>
                            <span className="label">Avec email</span>
                            <span className="value">{edition.stats.withEmail}</span>
                        </StatRow>
                        <StatRow highlight>
                            <span className="label">Sur la plateforme</span>
                            <span className="value">{edition.stats.onPlatform}</span>
                        </StatRow>
                        <StatRow highlight>
                            <span className="label">Projet dans l'annuaire</span>
                            <span className="value">{edition.stats.withProject}</span>
                        </StatRow>
                        <StatRow>
                            <span className="label">% inscriptions</span>
                            <span className="value">
                                {edition.stats.total > 0
                                    ? `${Math.round((edition.stats.onPlatform / edition.stats.total) * 100)}%`
                                    : '0%'}
                            </span>
                        </StatRow>
                        <StatRow>
                            <span className="label">Source</span>
                            <span className="value" style={{ fontSize: 12, color: '#999' }}>
                                {edition.source === 'supabase' ? 'Supabase' : 'MongoDB'}
                            </span>
                        </StatRow>
                    </EditionCard>
                ))}
            </EditionsGrid>

            <ExportSection>
                <p style={{ color: 'white', fontSize: 18, marginBottom: 20, fontWeight: 500 }}>
                    Export complet des 3 premières éditions ({editions.reduce((sum, e) => sum + e.stats.total, 0)} lauréats)
                </p>
                <ExportButton onClick={exportToXLSX} disabled={exporting || editions.length === 0}>
                    {exporting ? 'Export en cours...' : 'Télécharger XLSX'}
                </ExportButton>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 12 }}>
                    Un onglet par édition + onglet résumé
                </p>
            </ExportSection>
        </Container>
    );
};

export default FD100EditionsExport;
